import React, { useState } from 'react';
import { Card, Input, Button } from '../../components/ui';
import { AlertTriangle, TrendingUp, Users } from 'lucide-react';
import { cn } from '../../lib/utils';

export function CommunityLandTrustCalculatorTool() {
  const [marketValue, setMarketValue] = useState('350000');
  const [landValue, setLandValue] = useState('80000');
  const [affordablePrice, setAffordablePrice] = useState('210000');
  const [amiTarget, setAmiTarget] = useState('80');
  const [resaleCapRate, setResaleCapRate] = useState('2');
  const [years, setYears] = useState('10');

  const mv = parseFloat(marketValue) || 0;
  const lv = parseFloat(landValue) || 0;
  const ap = parseFloat(affordablePrice) || 0;
  const cap = parseFloat(resaleCapRate) || 0;
  const y = parseInt(years) || 0;

  const requiredSubsidy = Math.max(0, mv - ap);
  const percentSubsidy = mv > 0 ? (requiredSubsidy / mv) * 100 : 0;
  
  const futureMarketValue = mv * Math.pow(1.03, y); // assume 3% standard market appreciation
  const futureResaleValue = ap * Math.pow(1 + (cap / 100), y);
  
  const equityGained = futureResaleValue - ap;
  const totalSubsidyRetained = futureMarketValue - futureResaleValue;

  return (
    <div className="grid lg:grid-cols-2 gap-8 text-left font-sans">
      <div className="space-y-6">
        <Card className="p-6 space-y-4 rounded-none border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] bg-white dark:bg-black">
          <h3 className="text-xl font-black font-display uppercase tracking-tighter">Initial Purchase Setup</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-black dark:text-white mb-1 block">Appraised Market Value ($)</label>
              <Input type="number" className="rounded-none border-2 border-black dark:border-white focus:ring-0 focus:border-[#0044FF]" value={marketValue} onChange={e => setMarketValue(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-black dark:text-white mb-1 block">Est. Land Value ($)</label>
              <Input type="number" className="rounded-none border-2 border-black dark:border-white focus:ring-0 focus:border-[#0044FF]" value={landValue} onChange={e => setLandValue(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-black dark:text-white mb-1 block">Target Purchase Price ($)</label>
              <Input type="number" className="rounded-none border-2 border-black dark:border-white focus:ring-0 focus:border-[#0044FF]" value={affordablePrice} onChange={e => setAffordablePrice(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-black dark:text-white mb-1 block">Target AMI (%)</label>
              <Input type="number" className="rounded-none border-2 border-black dark:border-white focus:ring-0 focus:border-[#0044FF]" value={amiTarget} onChange={e => setAmiTarget(e.target.value)} />
            </div>
          </div>
        </Card>
        
        <Card className="p-6 space-y-4 rounded-none border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] bg-white dark:bg-black">
           <h3 className="text-xl font-black font-display uppercase tracking-tighter">Resale Restriction Terms</h3>
           <div className="grid grid-cols-2 gap-4">
             <div>
               <label className="text-[10px] font-black uppercase tracking-widest text-black dark:text-white mb-1 block">Annual Equity Cap (%)</label>
               <Input type="number" step="0.1" className="rounded-none border-2 border-black dark:border-white focus:ring-0 focus:border-[#0044FF]" value={resaleCapRate} onChange={e => setResaleCapRate(e.target.value)} />
             </div>
             <div>
               <label className="text-[10px] font-black uppercase tracking-widest text-black dark:text-white mb-1 block">Projection Years</label>
               <Input type="number" className="rounded-none border-2 border-black dark:border-white focus:ring-0 focus:border-[#0044FF]" value={years} onChange={e => setYears(e.target.value)} />
             </div>
           </div>
        </Card>
      </div>

      <div className="space-y-6">
        <Card className="p-6 rounded-none border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] bg-[#FFD600] text-black">
           <span className="text-[12px] uppercase font-black tracking-widest block mb-2 border-b-2 border-black pb-2">Required Initial Subsidy</span>
           <p className="text-5xl font-black font-display tracking-tighter mt-4">${requiredSubsidy.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
           <span className="text-sm font-bold block mt-2 px-3 py-1 bg-black text-white inline-block">({percentSubsidy.toFixed(1)}% of Market Value)</span>
           <p className="text-sm font-bold mt-4 leading-relaxed">
             This represents the gap between the fair market value and the target affordable price for a {amiTarget}% AMI household. 
           </p>
        </Card>
        
        <Card className="p-6 rounded-none border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] bg-white dark:bg-black flex flex-col justify-between">
           <div>
             <span className="text-[12px] uppercase font-black text-black dark:text-white block mb-4 tracking-widest border-b-2 border-black dark:border-white pb-2">Resale Projection (Year {y})</span>
             <div className="space-y-3 font-mono text-sm font-bold text-black dark:text-white">
               <div className="flex justify-between items-center py-2 border-b border-black/10 dark:border-white/10">
                 <span>Est. Open Market Value (3% gr/yr)</span>
                 <span>${futureMarketValue.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
               </div>
               <div className="flex justify-between items-center py-2 border-b border-black/10 dark:border-white/10">
                 <span className="text-[#0044FF]">Max Allowable Resale Price</span>
                 <span className="text-[#0044FF]">${futureResaleValue.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
               </div>
               <div className="flex justify-between items-center py-2 pt-4">
                 <span>Seller's Earned Equity</span>
                 <span className="bg-[#FFD600] text-black px-2 py-0.5">+${equityGained.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
               </div>
             </div>
           </div>
           
           <div className="mt-6 pt-6 border-t-[4px] border-black dark:border-white relative overflow-hidden">
             <div className="absolute top-0 right-0 -mr-4 -mt-4 w-16 h-16 bg-[#0044FF] pointer-events-none" />
             <span className="text-[12px] uppercase font-black text-black dark:text-white block mb-2 tracking-widest relative z-10">Preserved Affordability Subsidy</span>
             <p className="text-3xl font-black font-display tracking-tighter text-[#0044FF] relative z-10">${totalSubsidyRetained.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
             <p className="text-sm font-bold mt-2 relative z-10">Value retained in the home to subsidize the next lower-income buyer.</p>
           </div>
        </Card>
      </div>
    </div>
  );
}

